import React, { useState, useEffect } from 'react';
import { 
  Box, Card, CardContent, Grid, Typography, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Button, CircularProgress
} from '@mui/material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  LineChart, Line, PieChart, Pie, Cell
} from 'recharts';
import { DatePicker } from '@mui/x-date-pickers';
import { obterRelatorioGeral } from '../../services/relatorioService';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

const RelatorioGeralPage = () => {
  const [relatorio, setRelatorio] = useState(null);
  const [filtros, setFiltros] = useState({
    dataInicio: null,
    dataFim: null
  });
  const [loading, setLoading] = useState(false);

  const fetchRelatorio = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filtros.dataInicio) params.append('dataInicio', filtros.dataInicio.toISOString());
      if (filtros.dataFim) params.append('dataFim', filtros.dataFim.toISOString());
      
      const data = await obterRelatorioGeral(params);
      setRelatorio(data);
    } catch (error) {
      console.error('Erro ao buscar relatório:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRelatorio();
  }, []);

  if (loading || !relatorio) {
    return <CircularProgress />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Relatório Geral do Sistema
        </Typography>

        {/* Filtros de Data */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <DatePicker
              label="Data Início"
              value={filtros.dataInicio}
              onChange={(date) => setFiltros({...filtros, dataInicio: date})}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <DatePicker
              label="Data Fim"
              value={filtros.dataFim}
              onChange={(date) => setFiltros({...filtros, dataFim: date})}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button variant="contained" onClick={fetchRelatorio}>
              Atualizar
            </Button>
          </Grid>
        </Grid>

        {/* Cards de Resumo */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6">Total Instituições</Typography>
                <Typography variant="h4">{relatorio.instituicoes}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6">Total Estudantes</Typography>
                <Typography variant="h4">{relatorio.estudantes}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6">Total Pré-Inscrições</Typography>
                <Typography variant="h4">{relatorio.preInscricoes}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6">Total Pagamentos</Typography>
                <Typography variant="h4">
                  {new Intl.NumberFormat('pt-AO', { 
                    style: 'currency', 
                    currency: 'AOA' 
                  }).format(relatorio.pagamentos[0]?.valorTotal || 0)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* Gráficos e Tabelas */}
      <Grid container spacing={3}>
        {/* Gráfico de Cursos Populares */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Cursos mais Populares
            </Typography>
            <BarChart
              width={500}
              height={300}
              data={relatorio.cursosPopulares}
              margin={{top: 5, right: 30, left: 20, bottom: 5}}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="nome" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="totalPreInscricoes" fill="#8884d8" name="Total de Pré-inscrições" />
            </BarChart>
          </Paper>
        </Grid>

        {/* Instituições mais Ativas */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Instituições mais Ativas
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Instituição</TableCell>
                    <TableCell align="right">Total Pré-inscrições</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {relatorio.instituicoesAtivas.map((inst) => (
                    <TableRow key={inst.nome}>
                      <TableCell>{inst.nome}</TableCell>
                      <TableCell align="right">{inst.totalPreInscricoes}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Gráfico de Pagamentos */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Resumo de Pagamentos
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle1">Quantidade Total</Typography>
                    <Typography variant="h5">{relatorio.pagamentos[0]?.quantidade || 0}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle1">Valor Total</Typography>
                    <Typography variant="h5">
                      {new Intl.NumberFormat('pt-AO', { 
                        style: 'currency', 
                        currency: 'AOA' 
                      }).format(relatorio.pagamentos[0]?.valorTotal || 0)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default RelatorioGeralPage;